import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchData } from 'services/api/fetchData';
import { deleteData } from 'services/api/deleteData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import DeleteModal from 'components/modals/DeleteModal';
import PrimaryButton from 'components/buttons/PrimaryButton';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from 'services/utils/dateTimeUtils';
import { formatDecimalToHoursAndMinutes } from 'services/utils/convertDecimalToHoursAndMinutes';
import EmployeeTimeTableForm from './EmployeeTimeTableForm';
import { EmployeeTimeTableType, EmployeeTimeTableDayType, DAYS_OF_WEEK } from './EmployeeTypes';

interface EmployeeTimeTableTabProps {
    itemId: string;
}

const EmployeeTimeTableTab: React.FC<EmployeeTimeTableTabProps> = ({ itemId }) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { userLocale } = useSettings();
    const { initializeModal } = useModal();
    const [timetables, setTimetables] = useState<EmployeeTimeTableType[]>([]);
    const [loading, setLoading] = useState(true);

    // Fetches the timetable history of this employee
    const fetchTimetables = useCallback(async () => {
        const response = await fetchData({ apiUrl: 'get_employeetimetable_list', params: { employee: itemId }, handleLogout });
        setTimetables(response?.results || []);
        setLoading(false);
        // eslint-disable-next-line
    }, [itemId]);

    // Fetch the timetables on mount
    useEffect(() => {
        fetchTimetables();
    }, [fetchTimetables]);

    // Opens the timetable form modal, either to add a new one or to edit a not yet started one
    const openTimetableForm = (timetable?: EmployeeTimeTableType) => {
        initializeModal(
            <EmployeeTimeTableForm employeeId={itemId} timetable={timetable ?? null} />,
            { modalSize: 'medium-large', onSuccess: () => fetchTimetables() }
        );
    };

    // Opens the delete confirmation modal and marks the timetable as deleted on approval
    const handleDelete = (timetable: EmployeeTimeTableType) => {
        initializeModal(
            <DeleteModal
                onConfirmDelete={async () => {
                    const response = await deleteData({ apiObject: 'employeetimetable', itemId: timetable.id });
                    if (response?.status === 204) fetchTimetables();
                }}
            />,
            { modalSize: 'extra-small' }
        );
    };

    // Formats a stored time value without its seconds
    const formatTimeValue = (value: string | null): string => {
        return value ? value.slice(0, 5) : '';
    };

    // Formats a decimal amount of hours as hours and minutes
    const formatHours = (value: number | string | null): string => {
        if (value === null || value === '') return '';
        return formatDecimalToHoursAndMinutes(value);
    };

    // Determines wheter a day of the timetable counts as a working day
    const isWorkingDay = (day?: EmployeeTimeTableDayType): boolean => {
        if (!day) return false;
        const hasHours = day.total_hours !== null && parseFloat(day.total_hours.toString()) > 0;
        const hasWindow = !!day.start_time && !!day.end_time;
        return hasHours || hasWindow;
    };

    // Sums the total hours of the days of a week
    const calculateWeekTotal = (timetable: EmployeeTimeTableType, weekNumber: 1 | 2): number => {
        return timetable.days
            .filter(day => day.week_number === weekNumber && day.total_hours !== null)
            .reduce((total, day) => total + parseFloat(day.total_hours!.toString()), 0);
    };

    // Determines wheter the timetable is currently active, still to come or already expired
    const determineStatusLabel = (timetable: EmployeeTimeTableType): string => {
        const today = new Date().toISOString().slice(0, 10);
        if (timetable.start_date && timetable.start_date > today) return 'employee.timetable.status_future';
        if (timetable.end_date && timetable.end_date < today) return 'employee.timetable.status_past';
        return 'employee.timetable.status_current';
    };

    // Formats the validity period of a timetable
    const formatPeriod = (timetable: EmployeeTimeTableType): string => {
        const start = timetable.start_date ? formatDate(timetable.start_date, userLocale, 'writtenMonth') : t('employee.timetable.start_date_placeholder');
        const end = timetable.end_date ? formatDate(timetable.end_date, userLocale, 'writtenMonth') : t('employee.timetable.end_date_placeholder');
        return `${start} - ${end}`;
    };

    // Renders the days of one week of a timetable in view mode
    const renderWeek = (timetable: EmployeeTimeTableType, weekNumber: 1 | 2) => {
        const leaveBudget = weekNumber === 1 ? timetable.week1_leave_hours_budget : timetable.week2_leave_hours_budget;

        return (
            <div className='timetable-week' key={weekNumber}>
                {timetable.has_second_week && (
                    <div className='week-label'>
                        {t(weekNumber === 1 ? 'employee.timetable.week_1_label' : 'employee.timetable.week_2_label')}
                    </div>
                )}
                <div className='timetable-day-row header'>
                    <span></span>
                    <span>{t('employee.timetable.start_time_label')}</span>
                    <span>{t('employee.timetable.end_time_label')}</span>
                    <span>{t('employee.timetable.break_duration_label')}</span>
                    <span>{t('employee.timetable.total_hours_label')}</span>
                </div>
                {DAYS_OF_WEEK.map(dayOption => {
                    const day = timetable.days.find(d => d.week_number === weekNumber && d.day_of_week === dayOption.value);
                    const working = isWorkingDay(day);

                    return (
                        <div className={`timetable-day-row ${working ? '' : 'day-off'}`} key={dayOption.value}>
                            <span className='day-label'>{t(dayOption.label)}</span>
                            {working ? (
                                <>
                                    <span>{formatTimeValue(day!.start_time) || '-'}</span>
                                    <span>{formatTimeValue(day!.end_time) || '-'}</span>
                                    <span>{formatHours(day!.break_duration) || '-'}</span>
                                    <span>{formatHours(day!.total_hours) || '-'}</span>
                                </>
                            ) : (
                                <span className='day-off-label'>{t('employee.timetable.day_off_label')}</span>
                            )}
                        </div>
                    );
                })}
                <div className='timetable-week-footer'>
                    <span>
                        {t('employee.timetable.week_total_label')}: <strong>{formatDecimalToHoursAndMinutes(calculateWeekTotal(timetable, weekNumber))}</strong>
                    </span>
                    {leaveBudget !== null && leaveBudget !== '' && (
                        <span>
                            {t('employee.timetable.leave_hours_budget_label')}: <strong>{formatDecimalToHoursAndMinutes(leaveBudget)}</strong>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Render nothing until the timetables are loaded
    if (loading) return null;

    return (
        <div className='employee-timetable-tab'>
            <div className='timetable-tab-header'>
                <PrimaryButton onClick={() => openTimetableForm()} label='employee.timetable.add_timetable_button' size='small' />
            </div>

            {timetables.length === 0 && (
                <div className='card empty-card-message'>
                    <p>{t('employee.timetable.no_timetables_message')}</p>
                </div>
            )}

            {timetables.map(timetable => (
                <div className='card timetable-card' key={timetable.id}>
                    <div className='timetable-card-header'>
                        <div className='timetable-period'>
                            <h5>{formatPeriod(timetable)}</h5>
                            <span className='timetable-subline'>
                                {t(determineStatusLabel(timetable))}
                                {' · '}
                                {t(timetable.has_second_week ? 'employee.timetable.two_week_schedule_label' : 'employee.timetable.one_week_schedule_label')}
                            </span>
                        </div>
                        <div className='timetable-actions'>
                            {timetable.is_editable && (
                                <IconButton
                                    tooltipText='employee.timetable.edit_timetable_button'
                                    onClick={() => openTimetableForm(timetable)}
                                    size='small'
                                    icon={<FontAwesomeIcon icon={faPen} />}
                                />
                            )}
                            <IconButton
                                tooltipText='employee.timetable.delete_button'
                                onClick={() => handleDelete(timetable)}
                                size='small'
                                icon={<FontAwesomeIcon icon={faTrash} />}
                            />
                        </div>
                    </div>
                    <div className='timetable-weeks'>
                        {renderWeek(timetable, 1)}
                        {timetable.has_second_week && renderWeek(timetable, 2)}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EmployeeTimeTableTab;
